import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePoll } from '../contexts/PollContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { ArrowLeft, Users, TrendingUp, Clock, CheckCircle } from 'lucide-react';

interface FetchedPoll {
  id: string;
  title: string;
  question: string;
  options: Array<{
    id: string;
    emoji: string;
    label?: string;
  }>;
  state: 'open' | 'active' | 'closed';
}

interface FetchedPollResults {
  results: Array<{
    optionId: string;
    emoji: string;
    label: string;
    votes: number;
    percentage: number;
    voters: string[];
  }>;
  totalVotes: number;
  completedAt?: string;
}

function PollRoom() {
  const {
    currentPoll,
    pollResults,
    pollEnded,
    submitVote,
    resetPollState,
    players
  } = usePoll();
  
  const { player } = usePlayer();
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  const [userVote, setUserVote] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  
  // State for fetched historical poll data
  const [fetchedPoll, setFetchedPoll] = useState<FetchedPoll | null>(null);
  const [fetchedResults, setFetchedResults] = useState<FetchedPollResults | null>(null);
  const [isLoadingPoll, setIsLoadingPoll] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // Use socket data if available, otherwise use fetched data
  const displayPoll = currentPoll || fetchedPoll;
  const displayResults = pollResults || fetchedResults;
  const isPastPoll = !currentPoll && fetchedPoll?.state === 'closed';

  // Check if current player is host by looking at players array
  const isHost = players.find(p => p.id === player?.id)?.isHost || false;

  // Fetch historical poll data when socket data is unavailable (past polls)
  useEffect(() => {
    const fetchHistoricalPoll = async () => {
      // Only fetch if socket poll is not available and we have a roomId (pollId) and haven't loaded yet
      if (!currentPoll && urlRoomId && !isLoadingPoll && !fetchedPoll) {
        console.log(`📊 Socket poll not available, fetching historical poll ${urlRoomId}`);
        setIsLoadingPoll(true);
        setFetchError('');
        
        try {
          const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
          
          // Fetch poll data
          const pollResponse = await fetch(`${serverUrl}/api/admin/polls/${urlRoomId}`);
          if (!pollResponse.ok) {
            throw new Error('Poll not found');
          }
          const pollData = await pollResponse.json();
          console.log(`📊 Fetched historical poll data:`, pollData);
          setFetchedPoll(pollData.poll);
          
          // Fetch poll results
          const resultsResponse = await fetch(`${serverUrl}/api/polls/${urlRoomId}/results-history`);
          if (resultsResponse.ok) {
            const resultsData = await resultsResponse.json();
            console.log(`📊 Fetched historical poll results:`, resultsData);
            setFetchedResults(resultsData.results);
          } else {
            console.warn(`⚠️ No results found for poll ${urlRoomId}`);
            // Set empty results
            setFetchedResults({
              results: pollData.poll.options.map((opt: any) => ({
                optionId: opt.id,
                emoji: opt.emoji,
                label: opt.label || '',
                votes: 0,
                percentage: 0,
                voters: []
              })),
              totalVotes: 0
            });
          }
        } catch (err) {
          console.error('❌ Error fetching historical poll:', err);
          setFetchError('Failed to load poll');
        } finally {
          setIsLoadingPoll(false);
        }
      }
    };
    
    fetchHistoricalPoll();
  }, [currentPoll, urlRoomId, fetchedPoll]);

  // Track user's vote from poll results - simple version
  useEffect(() => {
    if (displayResults && player?.id) {
      const votedOption = displayResults.results.find(result => 
        result.voters.includes(player.id)
      );
      setUserVote(votedOption?.optionId || null);
    }
  }, [displayResults, player?.id]);

  // Optimized handlers with useCallback to prevent re-renders
  const handleVote = useCallback((optionId: string) => {
    console.log('🗳️ Optimized handleVote:', optionId);
    // Add check for active state (only allow voting on active socket polls, not past polls)
    if (urlRoomId && player?.id && currentPoll && currentPoll.state === 'active' && !pollEnded && !isPastPoll) {
      console.log('✅ Submitting vote');
      submitVote(urlRoomId, player.id, optionId);
    }
  }, [urlRoomId, player?.id, currentPoll, pollEnded, submitVote, isPastPoll]);

  const handleEndPoll = useCallback(() => {
    console.log('🏁 End poll functionality not implemented yet');
    // TODO: Implement endPoll functionality
  }, []);

  const handleNewPoll = useCallback(() => {
    resetPollState();
  }, [resetPollState]);

  const getPlayerNickname = useCallback((playerId: string) => {
    return players.find(p => p.id === playerId)?.nickname || 'Unknown';
  }, [players]);

  // Debug logging
  console.log('🔍 PollRoom state:', {
    currentPoll: !!currentPoll,
    pollResults: !!pollResults,
    fetchedPoll: !!fetchedPoll,
    fetchedResults: !!fetchedResults,
    isPastPoll,
    pollEnded,
    userVote,
    urlRoomId,
    playerId: player?.id,
    isHost
  });

  // Show loading state when fetching historical poll
  if (isLoadingPoll) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading poll...</p>
        </div>
      </div>
    );
  }

  if (!displayPoll) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-sans font-semibold text-lg">Poll Not Found</h1>
          </div>
          <Card>
            <CardContent className="p-6 text-center">
              <p className="font-mono text-muted-foreground">{fetchError || 'This poll doesn\'t exist or has been removed.'}</p>
              <Button onClick={() => navigate('/past-games')} className="mt-4">
                Back to Past Games
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const totalVotes = displayResults?.totalVotes || 0;
  const getPercentage = (votes: number) => {
    return totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>{totalVotes}</span>
            </div>
            {isPastPoll || pollEnded ? (
              <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                Closed
              </Badge>
            ) : displayPoll.state === 'active' ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Active
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                Open
              </Badge>
            )}
          </div>
        </div>

        {/* Real-time Status - Only show for active polls */}
        {!isPastPoll && !pollEnded && (
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
              Connected • {players.length} participants
            </div>
          </div>
        )}

        {/* Poll Question */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-sans font-semibold text-xl leading-tight">{displayPoll.question}</CardTitle>
            <CardDescription className="font-mono">
              {isPastPoll 
                ? "Poll has ended - Results below" 
                : pollEnded 
                  ? "Poll has ended" 
                  : displayPoll.state === 'active' 
                    ? "Tap an option to cast your vote"
                    : "Waiting for poll to begin..."}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Waiting Status for Open State */}
        {displayPoll.state === 'open' && !pollEnded && !isPastPoll && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-800">
                <Clock className="h-5 w-5" />
                <span className="font-mono text-sm font-medium">
                  Poll is open. Waiting for admin to activate voting...
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Voting Status - Only show for active polls where user voted but hasn't toggled results yet */}
        {userVote && !showResults && !pollEnded && !isPastPoll && displayPoll.state === 'active' && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                <span className="font-mono text-sm font-medium">Vote recorded! Loading results...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Poll Options */}
        <div className="space-y-3 mb-6">
          {displayResults ? (
            displayResults.results.map((option) => {
              const percentage = getPercentage(option.votes);
              const isSelected = userVote === option.optionId;
              // Always show results for past polls and closed polls
              const showPercentage = isPastPoll || pollEnded || showResults || userVote;

              return (
                <Card key={option.optionId} className="relative overflow-hidden">
                  {showPercentage && (
                    <div
                      className="absolute inset-0 bg-primary/10 transition-all duration-1000 ease-out"
                      style={{ width: `${percentage}%` }}
                    />
                  )}
                  <CardContent className="p-0">
                    <Button
                      variant="ghost"
                      className={`w-full h-auto min-h-[5rem] p-4 text-left justify-start relative z-10 ${
                        isSelected ? "bg-primary/20" : "hover:bg-transparent"
                      } ${userVote || pollEnded || isPastPoll || displayPoll.state !== 'active' ? "cursor-default" : ""}`}
                      onClick={() => handleVote(option.optionId)}
                      disabled={pollEnded || isPastPoll || displayPoll.state !== 'active'}
                    >
                      <div className="flex flex-col gap-2 w-full">
                        {/* Top row: emoji and label */}
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{option.emoji}</span>
                          <span className="font-mono text-base font-medium flex-1">{option.label}</span>
                          {isSelected && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />}
                        </div>
                        {/* Bottom row: votes and percentage (only when visible) */}
                        {showPercentage && (
                          <div className="flex flex-col gap-1 pl-11">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-muted-foreground">{option.votes} vote{option.votes !== 1 ? 's' : ''}</span>
                              <Badge variant="secondary" className="font-mono">
                                {percentage}%
                              </Badge>
                            </div>
                            {/* Show voter names */}
                            {option.voters && option.voters.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {option.voters.map((voterId, idx) => {
                                  // Try to get nickname from voterNicknames map first, then fall back to players array
                                  const nickname = (option as any).voterNicknames?.[voterId] || getPlayerNickname(voterId);
                                  return (
                                    <span key={voterId}>
                                      {nickname}
                                      {idx < option.voters.length - 1 && ', '}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            displayPoll.options.map((option) => (
              <Card key={option.id} className="relative overflow-hidden">
                <CardContent className="p-0">
                  <Button
                    variant="ghost"
                    className="w-full h-auto min-h-[4rem] p-4 text-left justify-start relative z-10 hover:bg-transparent"
                    onClick={() => handleVote(option.id)}
                    disabled={pollEnded || isPastPoll || displayPoll.state !== 'active'}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{option.emoji}</span>
                      <span className="font-mono text-base font-medium">{option.label}</span>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Results Summary */}
        {(showResults || pollEnded || isPastPoll) && displayResults && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-sans font-semibold text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Poll Results
              </CardTitle>
              <CardDescription className="font-mono">Total votes: {totalVotes}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {displayResults.results
                  .sort((a, b) => b.votes - a.votes)
                  .map((option, index) => {
                    const percentage = getPercentage(option.votes);
                    return (
                      <div key={option.optionId} className="flex items-center gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-lg">{option.emoji}</span>
                          <span className="font-mono text-sm truncate">{option.label}</span>
                          {index === 0 && totalVotes > 0 && (
                            <Badge variant="default" className="text-xs">
                              Winner
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={percentage} className="w-16 h-2" />
                          <span className="font-mono text-xs text-muted-foreground min-w-[3rem] text-right">
                            {percentage}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          {userVote && !pollEnded && !isPastPoll && (
            <Button variant="outline" className="w-full bg-transparent" onClick={() => setShowResults(!showResults)}>
              {showResults ? "Hide Results" : "Show Results"}
            </Button>
          )}

          {isHost && !pollEnded && !isPastPoll && (
            <Button variant="destructive" className="w-full" onClick={handleEndPoll}>
              End Poll
            </Button>
          )}

          {isHost && pollEnded && !isPastPoll && (
            <Button className="w-full" onClick={handleNewPoll}>
              Create New Poll
            </Button>
          )}

          {isPastPoll ? (
            <Button variant="outline" className="w-full" onClick={() => navigate('/past-games')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Past Games
            </Button>
          ) : (
            <Button variant="ghost" className="w-full" onClick={() => navigate('/lobby')}>
              Back to Home
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PollRoom;
